import { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import io from 'socket.io-client';
import apiService from '../services/apiService';
import { AuthContext } from '../context/AuthContext';
import { FaPaperPlane } from 'react-icons/fa';

const socket = io(process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000', { autoConnect: false });

function FarmerWasteCollectorChat() {
  const { user, isAuthenticated } = useContext(AuthContext);
  const { consultationId } = useParams();
  const navigate = useNavigate();
  const [participants, setParticipants] = useState([]);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [chatLoading, setChatLoading] = useState(false); // Separate loading state for chat messages
  const [error, setError] = useState(null);
  const chatContainerRef = useRef(null);

  // Socket.IO connection and error handling
  useEffect(() => {
    socket.on('connect', () => {
      console.log('Socket.IO connected:', socket.id);
    });

    socket.on('connect_error', (err) => {
      console.error('Socket.IO connection error:', err);
      toast.error('Failed to connect to chat server');
    });

    socket.on('disconnect', (reason) => {
      console.log('Socket.IO disconnected:', reason);
    });

    return () => {
      socket.off('connect');
      socket.off('connect_error');
      socket.off('disconnect');
    };
  }, []);

  // Fetch participants (farmers for waste collectors, waste collectors for farmers)
  useEffect(() => {
    const fetchChatParticipants = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found for fetching chat participants');
        toast.error('Session expired. Please log in again.');
        navigate('/login');
        return;
      }

      try {
        setLoading(true);
        let response;
        if (user?.role === 'farmer') {
          console.log(`Fetching waste collectors for farmerId: ${user?.id}`);
          response = await apiService.get(`/chats/farmer/${user?.id}`);
        } else if (user?.role === 'waste_collector') {
          console.log(`Fetching farmers for wasteCollectorId: ${user?.id}`);
          response = await apiService.get(`/chats/waste-collector/${user?.id}`);
        } else {
          throw new Error('Invalid role for this chat');
        }
        const data = Array.isArray(response) ? response : [response];
        setParticipants(data);
        if (consultationId) {
          const participant = data.find((p) => p.consultationId === consultationId);
          if (participant) {
            setSelectedParticipant(participant);
          }
        }
      } catch (err) {
        console.error('Fetch chat participants error:', err);
        setError(err.response?.data?.error || 'Failed to load chat participants');
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated() && (user?.role === 'farmer' || user?.role === 'waste_collector')) {
      fetchChatParticipants();
    }
  }, [user, consultationId, navigate, isAuthenticated]);

  // Fetch chat history and connect WebSocket
  useEffect(() => {
    if (!consultationId || !selectedParticipant) return;

    const token = localStorage.getItem('token');
    if (!token) {
      console.error('No token found for fetching chat history');
      toast.error('Session expired. Please log in again.');
      navigate('/login');
      return;
    }

    const fetchMessages = async () => {
      try {
        setChatLoading(true);
        console.log(`Fetching chat history for consultationId: ${consultationId}`);
        const response = await apiService.get(`/chats/${consultationId}`);
        setMessages(Array.isArray(response) ? response : []);
        setError(null);
      } catch (err) {
        console.error('Fetch chat history error:', err);
        setError(err.response?.data?.error || 'Failed to load messages');
      } finally {
        setChatLoading(false);
      }
    };

    fetchMessages();

    console.log('Connecting Socket.IO with token');
    socket.auth = { token };
    socket.connect();
    socket.emit('joinChat', { consultationId, userId: user?.id });

    socket.on('receiveMessage', (message) => {
      console.log('Received message:', message);
      setMessages((prev) => [...prev, message]);
    });

    return () => {
      socket.off('receiveMessage');
      socket.disconnect();
    };
  }, [consultationId, selectedParticipant, user, navigate]);

  // Scroll to the bottom of the chat when new messages are added
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  // Authentication redirect
  if (!isAuthenticated()) {
    console.log('Not authenticated, redirecting to login');
    toast.error('Please log in to access chat.');
    return <Navigate to="/login" />;
  }

  // Role check
  if (user?.role !== 'farmer' && user?.role !== 'waste_collector') {
    console.log('Unauthorized role:', user?.role);
    toast.error('Access restricted to farmers and waste collectors.');
    return <Navigate to="/dashboard" />;
  }

  const sendMessage = async () => {
    if (!messageInput.trim() || !selectedParticipant) return;

    const token = localStorage.getItem('token');
    if (!token) {
      console.error('No token found for sending message');
      toast.error('Session expired. Please log in again.');
      navigate('/login');
      return;
    }

    const message = {
      consultationId,
      modelType: 'WasteRequest',
      senderId: user.id,
      receiverId: selectedParticipant.userId,
      content: messageInput,
    };

    console.log('Sending message:', message);
    socket.emit('sendMessage', message);
    setMessageInput('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') sendMessage();
  };

  // Format last message time
  const formatLastMessageTime = (date) => {
    if (!date) return '';
    const now = new Date();
    const messageDate = new Date(date);
    const isToday = now.toDateString() === messageDate.toDateString();
    if (isToday) {
      return messageDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return messageDate.toLocaleDateString();
  };

  // Handle participant selection with smooth transition
  const handleParticipantSelect = (participant) => {
    if (selectedParticipant?.consultationId !== participant.consultationId) {
      setChatLoading(true);
      setSelectedParticipant(participant);
      // Only navigate if the URL needs to change
      if (consultationId !== participant.consultationId) {
        navigate(`/farmer/chat/farmer-waste-collector/${participant.consultationId}`, { replace: true });
      }
    }
  };

  return (
    <div className="flex h-screen bg-gray-100 dark:bg-gray-900 p-4">
      {/* Left Panel: Participant List */}
      <div className="w-1/3 md:w-1/4 bg-white dark:bg-gray-800 rounded-lg shadow-lg mr-4 overflow-y-auto transition-all duration-300">
        <div className="p-4 border-b dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
            {user?.role === 'farmer' ? 'Waste Collectors' : 'Farmers'}
          </h2>
        </div>
        {loading ? (
          <div className="p-4 text-center text-gray-600 dark:text-gray-300">
            Loading {user?.role === 'farmer' ? 'waste collectors' : 'farmers'}...
          </div>
        ) : error ? (
          <div className="p-4 text-center text-red-600 dark:text-red-400">Error: {error}</div>
        ) : participants.length === 0 ? (
          <div className="p-4 text-center text-gray-600 dark:text-gray-300">
            No {user?.role === 'farmer' ? 'waste collectors' : 'farmers'} found.
          </div>
        ) : (
          <ul className="divide-y dark:divide-gray-700">
            {participants.map((p) => (
              <li
                key={p.consultationId}
                className={`p-4 flex items-start space-x-3 cursor-pointer transition-colors duration-200 ${
                  selectedParticipant?.consultationId === p.consultationId
                    ? 'bg-blue-100 dark:bg-blue-900/70 border-l-4 border-blue-500'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
                onClick={() => handleParticipantSelect(p)}
              >
                <img
                  src={
                    p.profilePicture
                      ? `${process.env.REACT_APP_API_BASE_URL}${p.profilePicture}`
                      : 'https://via.placeholder.com/40'
                  }
                  onError={(e) => (e.target.src = 'https://via.placeholder.com/40')}
                  alt={`${p.fullName}'s profile`}
                  className="w-12 h-12 rounded-full object-cover border-2 border-gray-200 dark:border-gray-600"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-baseline">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {p.fullName} {p.role === 'farmer' ? '(Farmer)' : '(Waste Collector)'}
                    </p>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {formatLastMessageTime(p.lastMessageTime)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 truncate">
                    {p.wasteRequestTitle} - {p.wasteRequestQuantity} {p.wasteRequestUnit} ({p.wasteRequestType})
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {p.lastMessage || 'No messages yet'}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Right Panel: Chat */}
      <div className="flex-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg flex flex-col transition-all duration-300">
        {selectedParticipant ? (
          chatLoading ? (
            <div className="flex-1 flex items-center justify-center text-gray-600 dark:text-gray-300">
              <svg
                className="animate-spin h-8 w-8 text-blue-500"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8z"
                ></path>
              </svg>
              <span className="ml-2">Loading messages...</span>
            </div>
          ) : error ? (
            <div className="flex-1 flex items-center justify-center text-red-600 dark:text-red-400">
              Error: {error}
            </div>
          ) : (
            <>
              {/* Chat Header */}
              <div className="p-4 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                <div className="flex items-center space-x-3">
                  <img
                    src={
                      selectedParticipant.profilePicture
                        ? `${process.env.REACT_APP_API_BASE_URL}${selectedParticipant.profilePicture}`
                        : 'https://via.placeholder.com/40'
                    }
                    onError={(e) => (e.target.src = 'https://via.placeholder.com/40')}
                    alt={`${selectedParticipant.fullName}'s profile`}
                    className="w-10 h-10 rounded-full object-cover border-2 border-gray-200 dark:border-gray-600"
                  />
                  <div>
                    <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                      {selectedParticipant.fullName} {selectedParticipant.role === 'farmer' ? '(Farmer)' : '(Waste Collector)'}
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      Waste Request: {selectedParticipant.wasteRequestTitle} - {selectedParticipant.wasteRequestQuantity}{' '}
                      {selectedParticipant.wasteRequestUnit} ({selectedParticipant.wasteRequestType})
                    </p>
                  </div>
                </div>
              </div>
              {/* Chat Messages */}
              <div
                ref={chatContainerRef}
                className="flex-1 overflow-y-auto p-4 bg-gray-50 dark:bg-gray-900 transition-opacity duration-300"
                style={{ maxHeight: 'calc(100vh - 200px)' }}
              >
                {messages.length === 0 ? (
                  <div className="text-center text-gray-600 dark:text-gray-300 flex-1 flex items-center justify-center">
                    No chat history available
                  </div>
                ) : (
                  messages.map((msg, index) => (
                    <div
                      key={index}
                      className={`flex ${msg.senderId._id === user.id ? 'justify-end' : 'justify-start'} mb-3`}
                    >
                      <div
                        className={`flex items-start space-x-2 max-w-[70%] ${
                          msg.senderId._id === user.id ? 'flex-row-reverse space-x-reverse' : ''
                        }`}
                      >
                        <img
                          src={
                            msg.senderId.profilePicture
                              ? `${process.env.REACT_APP_API_BASE_URL}${msg.senderId.profilePicture}`
                              : 'https://via.placeholder.com/30'
                          }
                          onError={(e) => (e.target.src = 'https://via.placeholder.com/30')}
                          alt={`${msg.senderId.fullName}'s profile`}
                          className="w-8 h-8 rounded-full object-cover mt-2"
                        />
                        <div
                          className={`p-3 rounded-2xl shadow-sm relative ${
                            msg.senderId._id === user.id
                              ? 'bg-blue-600 text-white bubble-tail-right'
                              : 'bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 bubble-tail-left'
                          }`}
                        >
                          <p className="text-xs font-medium">
                            {msg.senderId.fullName} {msg.senderId.role === 'farmer' ? '(Farmer)' : '(Waste Collector)'}
                          </p>
                          <p className="text-sm">{msg.content}</p>
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 group relative">
                            {new Date(msg.createdAt).toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                            <span className="absolute hidden group-hover:block bg-gray-800 text-white text-xs rounded p-1 -top-8">
                              {new Date(msg.createdAt).toLocaleString()}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              {/* Message Input */}
              <div className="p-4 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="flex-1 p-3 rounded-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Type a message..."
                  />
                  <button
                    onClick={sendMessage}
                    className="p-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors duration-200"
                  >
                    <FaPaperPlane size={18} />
                  </button>
                </div>
              </div>
            </>
          )
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-600 dark:text-gray-300">
            Select a {user?.role === 'farmer' ? 'waste collector' : 'farmer'} to start chatting
          </div>
        )}
      </div>

      {/* Inline CSS for Bubble Tails */}
      <style>
        {`
          .bubble-tail-left::before {
            content: '';
            position: absolute;
            left: -8px;
            top: 10px;
            width: 0;
            height: 0;
            border-top: 8px solid transparent;
            border-bottom: 8px solid transparent;
            border-right: 8px solid #ffffff;
          }
          .bubble-tail-right::before {
            content: '';
            position: absolute;
            right: -8px;
            top: 10px;
            width: 0;
            height: 0;
            border-top: 8px solid transparent;
            border-bottom: 8px solid transparent;
            border-left: 8px solid #2563eb;
          }
          .dark .bubble-tail-left::before {
            border-right-color: #374151;
          }
        `}
      </style>
    </div>
  );
}

export default FarmerWasteCollectorChat;